import React, { useMemo } from 'react';
import { $Formutil, connect, FormItem, FormItemComponentProps } from 'react-antd-formutil';
import HashLink from 'components/HashLink';
import { openNativeModal } from 'components/Modal';
import bankLogo from 'static/images/bank.png';
import arrowIcon from 'static/images/icons/icon-arrow.svg';
import classlist from 'utils/classlist';
import { getMaskText } from 'utils/util';
import CardList, { CardListProps } from './CardList';
import { BankAccountMainTypeMap } from './config';
import * as Tags from './Tags';
import { BankCardItemTypes } from './type';
import './style.scss';

export { Tags, BankAccountMainTypeMap };
export * from './type';

interface BankCardSelectProps extends CardListProps {
    onChange?: (id: number | string) => void;
    disabled?: boolean;
    value?: string | number;
    // 需要设置为表单值的银行卡信息字段
    filterFiledValue: keyof BankCardItemTypes;
}

const BankCardSelect: React.FC<BankCardSelectProps> = ({
    data,
    onEdit,
    onAdd,
    onChange,
    disabled,
    value,
    filterFiledValue,
    showStatusTag
}) => {
    const { bankName, bankAccount, status, needAuthenticated, bankAccountMainType } =
        data?.find(item => item[filterFiledValue] === value) || {};

    const handleCard = () => {
        if (!disabled) {
            openNativeModal({
                component: props => (
                    <CardList {...props} data={data} onEdit={onEdit} onAdd={onAdd} showStatusTag={showStatusTag} />
                ),
                maskClosable: true,
                closable: true,
                title: __('Choose Bank Card')
            })
                .result.then(info => {
                    if (info) {
                        onChange?.(info);
                    }
                })
                .catch(() => {});
        }
    };

    return (
        <div className="bank-card-select-root">
            {value && !!data?.length ? (
                <div className="card-box" onClick={handleCard}>
                    <img src={bankLogo} className="bank-logo" alt="" />
                    <div className="content">
                        <div className="bank-header">
                            <div className="bank-name" title={bankName}>
                                {bankName}
                            </div>
                            {typeof status === 'number' && showStatusTag && (
                                <Tags.BankCardAuthStatusTag status={status} needAuthenticated={needAuthenticated} />
                            )}
                        </div>
                        <div className="bank-account">
                            {bankAccountMainType ? `${BankAccountMainTypeMap[bankAccountMainType]} ` : ''}
                            {getMaskText(bankAccount)}
                        </div>
                    </div>
                    {!disabled && <img src={arrowIcon} className="arrow" alt="" />}
                </div>
            ) : onAdd ? (
                <HashLink
                    className="add-bank"
                    onClick={() => {
                        onAdd();
                    }}>
                    + {__('新增银行账户')}
                </HashLink>
            ) : null}
        </div>
    );
};

const BankCardSelectFormItem: React.FC<
    BankCardSelectProps & {
        $formutil: $Formutil;
        // 提醒用户的信息，与error不同、此字段没有强校验仅用于提醒用户
        remind?: {
            msg: React.ReactNode; // 提醒的信息
            btn?: React.ReactNode; // 操作的按钮
        };
        customErrorConfigMap?: Record<
            string,
            {
                msg: React.ReactNode;
                btn: React.ReactNode;
            }
        >;
        formItemConfig?: Omit<FormItemComponentProps, 'children'>;
        defaultBankInfo?: BankCardItemTypes;
    }
> = ({ formItemConfig, $formutil, customErrorConfigMap, remind, ...rest }) => {
    const { $errors, $getFirstError } = $formutil;
    const { name } = formItemConfig || {};
    const { data, defaultBankInfo, onChange, filterFiledValue } = rest;

    const renderError = () => {
        if (!customErrorConfigMap || !$getFirstError(name).startsWith('CUSTOM_ERROR')) {
            return $getFirstError(name);
        }

        const customErrorConfig = customErrorConfigMap[$getFirstError(name)];

        return (
            <div className="operable-error-tips">
                {customErrorConfig.msg}
                {customErrorConfig.btn && <span className="operable-btn">{customErrorConfig.btn}</span>}
            </div>
        );
    };

    const defaultValue = useMemo(() => {
        return defaultBankInfo?.[filterFiledValue] || data?.[0]?.[filterFiledValue] || undefined;
    }, [data, filterFiledValue, defaultBankInfo]);

    return (
        <div
            className={classlist('bank-select-wrapper', {
                'has-error': name ? $errors[name] : false,
                'ant-form-item-has-error': name ? $errors[name] : false
            })}>
            <FormItem
                errorLevel="off"
                {...formItemConfig}
                $defaultValue={defaultValue}
                getValueFromEvent={(info: BankCardItemTypes) => {
                    return filterFiledValue ? info?.[filterFiledValue] : info;
                }}
                $onFieldChange={onChange}>
                <BankCardSelect {...rest} />
            </FormItem>
            {name && !!$errors[name] ? (
                <div className="ant-form-item-explain-error">{renderError()}</div>
            ) : !!remind ? (
                <div className="operable-info-tips">
                    {remind.msg}
                    {remind.btn && <span className="operable-btn">{remind.btn}</span>}
                </div>
            ) : null}
        </div>
    );
};

export default connect(BankCardSelectFormItem);
